import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import Header from '../components/layout/Header';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { 
  Activity,
  CheckCircle,
  PlusCircle,
  Pencil,
  Trash2,
  FileText,
  Upload,
  LogIn,
  User,
  Clock,
  Building2,
  Shield,
  Megaphone,
  DollarSign,
  Users,
  Filter,
  RefreshCw
} from 'lucide-react';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UserTimeline = () => {
  const [activities, setActivities] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [functionFilter, setFunctionFilter] = useState('all');
  const [limit, setLimit] = useState(50);

  const fetchActivities = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit };
      if (typeFilter !== 'all') params.activity_type = typeFilter;
      if (functionFilter !== 'all') params.function = functionFilter;
      
      const [activitiesRes, summaryRes] = await Promise.all([
        axios.get(`${API}/activity-log`, { params }),
        axios.get(`${API}/activity-log/summary`, { params: { days: 7 } })
      ]);
      
      setActivities(activitiesRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Failed to fetch activities:', error);
    } finally {
      setLoading(false);
    }
  }, [typeFilter, functionFilter, limit]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const getActivityIcon = (type) => {
    const iconMap = {
      action_created: <PlusCircle className="w-4 h-4 text-emerald-600" />,
      action_updated: <Pencil className="w-4 h-4 text-blue-600" />,
      action_deleted: <Trash2 className="w-4 h-4 text-rose-600" />,
      action_completed: <CheckCircle className="w-4 h-4 text-emerald-600" />,
      note_created: <FileText className="w-4 h-4 text-violet-600" />,
      note_updated: <FileText className="w-4 h-4 text-blue-600" />,
      data_uploaded: <Upload className="w-4 h-4 text-amber-600" />,
      kpi_updated: <Activity className="w-4 h-4 text-blue-600" />,
      user_login: <LogIn className="w-4 h-4 text-slate-600" />,
    };
    return iconMap[type] || <Activity className="w-4 h-4 text-slate-400" />;
  };

  const getActivityColor = (type) => {
    const colorMap = {
      action_created: 'bg-emerald-100 border-emerald-200',
      action_updated: 'bg-blue-100 border-blue-200',
      action_deleted: 'bg-rose-100 border-rose-200',
      action_completed: 'bg-emerald-100 border-emerald-200',
      note_created: 'bg-violet-100 border-violet-200',
      note_updated: 'bg-blue-100 border-blue-200',
      data_uploaded: 'bg-amber-100 border-amber-200',
      kpi_updated: 'bg-blue-100 border-blue-200',
      user_login: 'bg-slate-100 border-slate-200',
    };
    return colorMap[type] || 'bg-slate-100 border-slate-200';
  };

  const getFunctionIcon = (func) => {
    const iconMap = {
      brokerage: Building2,
      mga: Shield,
      marketing: Megaphone,
      finance: DollarSign,
      people: Users,
    };
    const Icon = iconMap[func] || Activity;
    return <Icon className="w-3 h-3" />;
  };

  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  const activityTypes = [
    { value: 'all', label: 'All Activities' },
    { value: 'action_created', label: 'Actions Created' },
    { value: 'action_updated', label: 'Actions Updated' },
    { value: 'action_completed', label: 'Actions Completed' },
    { value: 'action_deleted', label: 'Actions Deleted' },
    { value: 'note_created', label: 'Notes Created' },
    { value: 'data_uploaded', label: 'Data Uploaded' },
  ];

  const functions = [
    { value: 'all', label: 'All Functions' },
    { value: 'brokerage', label: 'Brokerage' },
    { value: 'mga', label: 'MGA' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'finance', label: 'Finance' },
    { value: 'people', label: 'People' },
  ];

  if (loading) {
    return (
      <Layout>
        <Header title="Activity Timeline" subtitle="Track all user actions across the platform" />
        <div className="p-4 sm:p-6 space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <Skeleton key={i} className="h-24 rounded-lg" />
            ))}
          </div>
          <Skeleton className="h-12 w-full max-w-md" />
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map(i => (
              <Skeleton key={i} className="h-20 rounded-lg" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Header title="Activity Timeline" subtitle="Track all user actions across the platform" />
      
      <div className="p-4 sm:p-6 space-y-6" data-testid="user-timeline">
        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-500">Last 7 Days</span>
                <Activity className="w-5 h-5 text-slate-400" />
              </div>
              <p className="text-2xl font-bold text-slate-900 mt-2">{summary.total_activities}</p>
              <p className="text-xs text-slate-500 mt-1">Total activities</p>
            </div>
            
            <div className="bg-amber-50 rounded-lg border border-amber-200 p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-amber-700">Data Uploads</span>
                <Upload className="w-5 h-5 text-amber-600" />
              </div>
              <p className="text-2xl font-bold text-amber-800 mt-2">
                {summary.by_type?.data_upload || 0}
              </p>
              <p className="text-xs text-amber-600 mt-1">New data entries</p>
            </div>
            
            <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-blue-700">KPI Updates</span>
                <Pencil className="w-5 h-5 text-blue-600" />
              </div>
              <p className="text-2xl font-bold text-blue-800 mt-2">
                {summary.by_type?.kpi_updated || 0}
              </p>
              <p className="text-xs text-blue-600 mt-1">Values modified</p>
            </div>
            
            <div className="bg-violet-50 rounded-lg border border-violet-200 p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-violet-700">Active Users</span>
                <Users className="w-5 h-5 text-violet-600" />
              </div>
              <p className="text-2xl font-bold text-violet-800 mt-2">
                {summary.top_contributors?.length || 0}
              </p>
              <p className="text-xs text-violet-600 mt-1">Contributors</p>
            </div>
          </div>
        )}

        {/* Top Contributors */}
        {summary?.top_contributors && summary.top_contributors.length > 0 && (
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Top Contributors (Last 7 Days)</h3>
            <div className="flex flex-wrap gap-2">
              {summary.top_contributors.slice(0, 5).map((contributor, idx) => (
                <div 
                  key={contributor.name}
                  className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-full"
                >
                  <div className={cn(
                    "w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white",
                    idx === 0 ? "bg-amber-500" : idx === 1 ? "bg-slate-400" : idx === 2 ? "bg-amber-700" : "bg-slate-300"
                  )}>
                    {idx + 1}
                  </div>
                  <span className="text-sm font-medium text-slate-700">{contributor.name}</span>
                  <span className="text-xs text-slate-500 bg-white px-2 py-0.5 rounded-full">
                    {contributor.count} actions
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400" />
            <span className="text-sm text-slate-500">Filter:</span>
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[180px]" data-testid="type-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {activityTypes.map(t => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={functionFilter} onValueChange={setFunctionFilter}>
            <SelectTrigger className="w-[160px]" data-testid="function-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {functions.map(f => (
                <SelectItem key={f.value} value={f.value}>
                  {f.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchActivities}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-slate-200" />
          
          {/* Timeline items */}
          <div className="space-y-4">
            {activities.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <Activity className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No activities found</p>
                <p className="text-sm mt-1">Activities will appear here as users take actions</p>
              </div>
            ) : (
              activities.map((activity, index) => (
                <div key={activity.id} className="relative pl-14">
                  {/* Timeline dot */}
                  <div className={cn(
                    "absolute left-4 w-5 h-5 rounded-full border-2 flex items-center justify-center bg-white",
                    getActivityColor(activity.activity_type)
                  )}>
                    {getActivityIcon(activity.activity_type)}
                  </div>
                  
                  {/* Activity card */}
                  <div className="bg-white rounded-lg border border-slate-200 p-4 hover:shadow-sm transition-shadow">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                      <div className="flex-1">
                        <p className="text-sm text-slate-900 font-medium">
                          {activity.description}
                        </p>
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          <div className="flex items-center gap-1 text-xs text-slate-500">
                            <User className="w-3 h-3" />
                            <span>{activity.username || activity.user_name}</span>
                          </div>
                          {activity.function && (
                            <div className="flex items-center gap-1 text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full capitalize">
                              {getFunctionIcon(activity.function)}
                              <span>{activity.function}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-1 text-xs text-slate-400">
                            <Clock className="w-3 h-3" />
                            <span>{formatRelativeTime(activity.created_at)}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Activity type badge */}
                      <div className={cn(
                        "text-xs px-2 py-1 rounded-full font-medium whitespace-nowrap",
                        activity.activity_type === 'action_completed' && "bg-emerald-100 text-emerald-700",
                        activity.activity_type === 'action_created' && "bg-blue-100 text-blue-700",
                        activity.activity_type === 'action_updated' && "bg-slate-100 text-slate-700",
                        activity.activity_type === 'action_deleted' && "bg-rose-100 text-rose-700",
                        activity.activity_type === 'note_created' && "bg-violet-100 text-violet-700",
                        activity.activity_type === 'data_uploaded' && "bg-amber-100 text-amber-700"
                      )}>
                        {activity.activity_type.replace(/_/g, ' ')}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Load More */}
        {activities.length >= limit && (
          <div className="text-center">
            <Button 
              variant="outline" 
              onClick={() => setLimit(prev => prev + 50)}
            >
              Load More
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default UserTimeline;
